// This is the current (V2) Unbox safety flow. The original V1 implementation
// is kept for reference at packages/frontend/src/pages/safety-beta.
import { zodResolver } from "@hookform/resolvers/zod";
import type { MerchantRiskCatalogItem } from "@unbox/api";
import { Feather, InfoCircle, List, ShieldTick } from "@untitledui/icons";
import { type PropsWithChildren, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod/v3";
import { useBulkAddFlag } from "@/api/assurance/bulk-add-flag";
import { useMerchantRisksList } from "@/api/merchant-risks";
import { useMessageTemplates } from "@/api/message-templates";
import { SAFETY_POLICY_LABELS, SAFETY_POLICY_PRIORITY, useSafetyPolicies } from "@/api/safety-policies";
import { useSequence, useVideoPolicyRuns } from "@/api/sequences";
import { Button } from "@/components/base/buttons/button";
import { Select } from "@/components/base/select/select";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { buildRiskGroupsFromCatalog, matchesRisk } from "@/components/safety-config/risk-groups";
import { RiskSelect } from "@/components/safety-config/risk-select";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { stepsToActionSteps } from "@/pages/safety-beta/components/policy-steps";
import type { ActionStep } from "@/pages/safety-beta/components/VideoDetail";
import { error, success, warning } from "@/providers/ToastProvider";
import { cx } from "@/utils/cx";
import { pluralize } from "@/utils/text";
import { PolicyRunAlertPanel } from "./policy-run-alert-panel";
import { resolvePolicyRunAlerts } from "./policy-run-alerts";

interface AddRiskModalWithPolicyProps extends PropsWithChildren {
  selectedVideos: string[];
  onSuccess: () => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  variant?: "bulk" | "single";
  disabledRiskIds?: string[];
}

const OTHER_OPTION_ID = "other";
const OTHER_RISK = { riskTier: 3, riskTypeShort: "Other" };

const POLICY_CHOICE_TOOLTIP =
  "Adding a risk here doesn't run a policy on its own. Choose Run a Policy to act on it now.";

const formSchema = z
  .object({
    riskId: z.string().min(1, "Risk is required"),
    explanation: z.string().min(1, "Explanation is required"),
    runPolicy: z.enum(["log", "policy"]),
    policyId: z.string().optional(),
  })
  .refine((values) => values.runPolicy !== "policy" || !!values.policyId, {
    message: "Select the policy to run",
    path: ["policyId"],
  });

const POLICY_CHOICES = [
  { value: "log" as const, id: "log-only", label: "Log it Only", icon: List },
  { value: "policy" as const, id: "run-policy", label: "Run a Policy", icon: Feather },
];

const PolicyStepList = ({ steps }: { steps: ActionStep[] }) => (
  <div className="flex flex-col">
    {steps.map((step, index) => (
      <div key={`${step.text}-${index}`} className="flex gap-2">
        <div className="w-3.5 shrink-0 flex flex-col items-center pt-1.5">
          <span
            className={cx(
              "rounded-full shrink-0 z-10",
              step.isSubtle ? "size-1.5 bg-unbox-sky" : "size-2 bg-utility-warning-500",
            )}
          />
          {index < steps.length - 1 && <span className="w-px flex-1 min-h-2 -mb-1.5 bg-border-primary" />}
        </div>
        <span className={cx("text-sm leading-5 pb-2", step.isSubtle ? "text-tertiary" : "text-secondary")}>
          {step.text}
        </span>
      </div>
    ))}
  </div>
);

export const AddRiskModalWithPolicy = ({
  selectedVideos,
  onSuccess,
  children,
  open: controlledOpen,
  onOpenChange,
  variant = "bulk",
  disabledRiskIds = [],
}: AddRiskModalWithPolicyProps) => {
  const isSingleVideo = variant === "single";
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: { riskId: "", explanation: "", runPolicy: "log" as const, policyId: undefined },
  });
  const { mutateAsync, isPending } = useBulkAddFlag();
  const { data: merchantRisks } = useMerchantRisksList();
  const { data: policies } = useSafetyPolicies();
  const { data: templates = [] } = useMessageTemplates();

  const catalog: MerchantRiskCatalogItem[] = (merchantRisks ?? []).map((mr) => ({
    ...mr.risk,
    tier: mr.tier ?? mr.risk.defaultTier,
    enabled: true,
  }));
  const riskGroups = buildRiskGroupsFromCatalog(catalog);
  const riskOptionGroups = [
    ...riskGroups,
    ...(isSingleVideo ? [{ id: OTHER_OPTION_ID, name: "", items: [{ key: OTHER_OPTION_ID, name: "Other" }] }] : []),
  ];
  const disabledRiskKeys = riskGroups
    .flatMap((group) => group.items)
    .filter((item) => disabledRiskIds.some((riskId) => matchesRisk(item, riskId)))
    .map((item) => item.key);

  const selectedRiskId = form.watch("riskId");
  const runPolicy = form.watch("runPolicy");
  const policyId = form.watch("policyId");

  const isOtherSelected = selectedRiskId === OTHER_OPTION_ID;
  const selectedRisk = riskGroups.flatMap((group) => group.items).find((item) => item.key === selectedRiskId);
  const riskName = isOtherSelected ? "Other" : (selectedRisk?.name ?? "Risk");

  const policyOptions = (policies ?? [])
    .slice()
    .sort((a, b) => SAFETY_POLICY_PRIORITY[a.type] - SAFETY_POLICY_PRIORITY[b.type])
    .map((policy) => ({ id: policy.id, label: SAFETY_POLICY_LABELS[policy.type], sequenceId: policy.sequenceId }));

  const configuredPolicyId = (merchantRisks ?? []).find((mr) => mr.riskId === selectedRiskId)?.safetyPolicyId;
  const [policyOverridden, setPolicyOverridden] = useState(false);

  useEffect(() => {
    if (policyOverridden) return;
    form.setValue("policyId", configuredPolicyId ?? undefined);
  }, [configuredPolicyId, policyOverridden]);

  const selectedPolicy = policyOptions.find((option) => option.id === policyId);
  const { data: sequence } = useSequence(selectedPolicy?.sequenceId ?? "", { enabled: !!selectedPolicy?.sequenceId });
  const steps = stepsToActionSteps(sequence?.steps ?? [], templates);

  const showPolicyBlock = !!selectedRiskId && runPolicy === "policy";
  const { data: policyRuns } = useVideoPolicyRuns(selectedVideos, { enabled: open && showPolicyBlock });
  const { alerts, remaining, startsSecondPolicy } = resolvePolicyRunAlerts({
    videoIds: selectedVideos,
    runs: policyRuns ?? [],
    selectedPolicyId: policyId,
    policyNameById: new Map(policyOptions.map((option) => [option.id, option.label])),
  });
  const pickerDisabled = isPending || (showPolicyBlock && remaining === 0);

  const resetForm = () => {
    form.reset();
    setPolicyOverridden(false);
  };

  const onSubmit = async (payload: z.infer<typeof formSchema>) => {
    const runsPolicy = payload.runPolicy === "policy" && !!payload.policyId;

    const result = await mutateAsync({
      videoIds: selectedVideos,
      ...(payload.riskId === OTHER_OPTION_ID ? OTHER_RISK : { riskId: payload.riskId }),
      explanation: payload.explanation,
      policyId: runsPolicy ? payload.policyId : undefined,
    });

    if (result.failed.length > 0) {
      error(
        isSingleVideo
          ? `Couldn't add "${riskName}"`
          : `Couldn't add "${riskName}" to ${pluralize(result.failed.length, "video", "videos")}`,
      );
    }
    if (result.successful.length === 0) return;

    const target = isSingleVideo ? "" : ` to ${pluralize(result.successful.length, "video", "videos")}`;
    if (result.policyFailed.length > 0) {
      warning(`"${riskName}" added${target}, but the ${selectedPolicy?.label} policy couldn't be started`);
    } else {
      success(
        runsPolicy
          ? `"${riskName}" added${target} and the ${selectedPolicy?.label} policy started`
          : `"${riskName}" added${target}`,
      );
    }

    onSuccess();
    resetForm();
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) resetForm();
        setOpen(next);
      }}
    >
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className="sm:max-w-[548px] max-h-[85vh] overflow-y-auto">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <DialogHeader>
              <DialogTitle>
                {isSingleVideo
                  ? "Add risk to this video"
                  : `Add risk to ${pluralize(selectedVideos.length, "selected video", "selected videos")}`}
              </DialogTitle>
            </DialogHeader>

            <FormField
              control={form.control}
              name="riskId"
              render={({ field, fieldState }) => (
                <FormItem>
                  <RiskSelect
                    isRequired
                    label="Risk"
                    groups={riskOptionGroups}
                    value={field.value || undefined}
                    onChange={field.onChange}
                    disabledKeys={disabledRiskKeys}
                    disabled={isPending}
                    isInvalid={!!fieldState.error}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="explanation"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Explanation</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Explain why this content is flagged..."
                      rows={6}
                      {...field}
                      disabled={isPending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {selectedRiskId && (
              <FormField
                control={form.control}
                name="runPolicy"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <div className="flex items-center justify-between">
                      <FormLabel>When the risk is added</FormLabel>
                      <Tooltip title={POLICY_CHOICE_TOOLTIP}>
                        <TooltipTrigger className="inline-flex cursor-pointer">
                          <InfoCircle className="size-5 text-fg-quaternary" />
                        </TooltipTrigger>
                      </Tooltip>
                    </div>
                    <FormControl>
                      <RadioGroup
                        onValueChange={field.onChange}
                        value={field.value}
                        className="grid grid-cols-2 gap-2"
                        disabled={isPending}
                      >
                        {POLICY_CHOICES.map((choice) => (
                          <label
                            key={choice.value}
                            htmlFor={choice.id}
                            className={cx(
                              "block rounded-xl bg-primary p-3",
                              isPending ? "cursor-not-allowed" : "cursor-pointer",
                              field.value === choice.value ? "border-2 border-brand" : "border border-secondary",
                            )}
                          >
                            <span className="flex items-start justify-between">
                              <FeaturedIcon size="sm" icon={choice.icon} color="gray" theme="modern" />
                              <RadioGroupItem value={choice.value} id={choice.id} />
                            </span>
                            <span className="mt-2 block text-sm font-medium text-secondary">{choice.label}</span>
                          </label>
                        ))}
                      </RadioGroup>
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            {selectedRiskId && runPolicy === "log" && (
              <div className="flex gap-2 rounded-lg bg-secondary p-3">
                <ShieldTick className="size-[18px] shrink-0 text-fg-quaternary" />
                <div className="flex flex-col gap-1">
                  <p className="text-sm font-medium text-secondary">Record risk - Nothing else happens</p>
                  <p className="text-sm text-tertiary">Collaboration keeps running and no creator is contacted</p>
                </div>
              </div>
            )}

            {showPolicyBlock && alerts.map((alert) => <PolicyRunAlertPanel key={alert.kind} alert={alert} />)}

            {showPolicyBlock && (
              <div className="flex flex-col gap-3 rounded-lg bg-[#fcf6f1] p-4">
                <FormField
                  control={form.control}
                  name="policyId"
                  render={({ field, fieldState }) => (
                    <FormItem>
                      <Select
                        validationBehavior="aria"
                        placeholder="Select policy"
                        items={policyOptions.map((option) => ({ id: option.id, label: option.label }))}
                        selectedKey={field.value ?? null}
                        onSelectionChange={(key) => {
                          setPolicyOverridden(true);
                          field.onChange(key as string);
                        }}
                        isDisabled={pickerDisabled}
                        isInvalid={!!fieldState.error}
                      >
                        {(item) => <Select.Item id={item.id} label={item.label ?? ""} />}
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <PolicyStepList steps={steps} />
              </div>
            )}

            <DialogFooter>
              <DialogClose asChild>
                <Button type="button" color="secondary" disabled={isPending}>
                  Cancel
                </Button>
              </DialogClose>
              <Button type="submit" disabled={isPending}>
                {isPending
                  ? "Adding..."
                  : startsSecondPolicy
                    ? "Add Risk & Start Second Policy"
                    : runPolicy === "policy"
                      ? "Add Risk & Action"
                      : "Add Risk"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
